import {
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Draggable,
  Eventcalendar,
  localeFr,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscEventConnection,
  MbscEventCreateEvent,
  MbscEventUpdatedEvent,
  MbscEventUpdateEvent,
  MbscResourceCollapseEvent,
  MbscResourceExpandEvent,
} from "@mobiscroll/react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import PaletteIcon from "@mui/icons-material/Palette";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReactSession } from "react-client-session";
import { useSearchParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import KanbanTaskCard from "../../../Components/Cards/KanbanTaskCard/kanban-task-card";
import ListPageHeader, {
  ListPageHeaderGroupActions,
  ListPageHeaderLeft,
  ListPageHeaderRight,
} from "../../../Components/Misc/ListPageHeader";
import { ModalContext } from "../../../Components/Modals";
import ModalEditTask, { ModalEditTaskElementProps } from "../../../Components/Modals/ModalEditTask/modal-edit-task";
import UserTaskPlanningPanel from "../../../Components/Panels/UserTaskPlanningPanel/user-task-planning-panel";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import { EventNotifierOperation } from "../../../Core/Api/notifier-client";
import { cacheService, calendarNotifier, planningUserTaskService, userService } from "../../../Core/Api/services";
import { DbObjectCreateOrUpdateInfos } from "../../../Core/Api/Types/db-object-create-or-update-infos";
import { DbOrderManualTaskPlanningInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-infos";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { GroupPlanningInfos } from "../../../Core/Api/Types/group-planning-infos";
import { GroupPlanningUserGroupInfos } from "../../../Core/Api/Types/group-planning-user-group-infos";
import { ManualTaskPlanningStatus } from "../../../Core/Api/Types/manual-task-planning-status";
import { PlanningMode } from "../../../Core/Api/Types/planning-mode";
import { PlanningTaskType } from "../../../Core/Api/Types/planning-task-type";
import { PlanningView } from "../../../Core/Api/Types/planning-view";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import ReactSessionKeys from "../../../Core/Utils/Constants/react-session-keys";
import "../../../Theme/CSS/Planning/planning-calendar.css";
import { Distinct, FindDuplicates, IsGuid } from "../../../Utils/helpers";
import { hexToRgbString, isColorDark } from "../../../Utils/Helpers/cssUtils";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { useTranslation } from "react-i18next";

interface Resource {
  id: string;
  name: string;
  eventCreation: boolean;
  collapsed: boolean;
  children: Resource[];
}

interface CalendarEvent extends MbscCalendarEvent {
  taskId: number;
  customerName: string;
  serviceName: string;
  taskName: string;
  assignedUserId: string | null;
  taskType: PlanningTaskType;
  // render props
  titleName: string;
  btCode: string;
  statusColor: string;
  eventAllDay?: boolean;
  dueDate: Date | null;
  mpDuration: number | null | undefined;
}

interface DraggableToDoTask {
  task: DbOrderManualTaskPlanningInfos;
  events: CalendarEvent[];
  connections: MbscEventConnection[];
}

function PlanningCalendar() {
  const { t } = useTranslation();
  const enqueueSnackbar = useCustomSnackbar();
  // Consts
  const nameOfUserId = useMemo(() => "_userId", []);
  const nameOfResourceId = useMemo(() => "_resourceId", []);
  const nameOfGroupId = useMemo(() => "_groupId", []);

  const currentUser = cacheService.GetUserInfo();
  const theme = useTheme();
  const defaultPlatformPlanningTaskDuration = cacheService.GetPlatformDefaultPlanningTaskDuration();

  // Calendar api
  const scheduleRef = useRef<Eventcalendar>(null);

  // #region states
  const [isLoading, setIsLoading] = useState(true);
  const [planningMode, setPlanningMode] = useState<PlanningMode>(PlanningMode.Timeline);
  const [planningView, setPlanningView] = useState<PlanningView>(PlanningView.Week);
  const [controlKeyDown, setControlKeyDown] = useState<boolean>(false);
  const clickEventCount = useRef<number>(0);
  const [expandDraggableTask, setExpandDraggableTask] = useState<boolean>(false); // Used to expand draggable task area

  // Filters states
  const [textFilter, setTextFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [taskNameFilter, setTaskNameFilter] = useState("");
  const [assignedUserFilter, setAssignedUserFilter] = useState("");
  const [colorFilter, setColorFilter] = useState<"project" | "status">("status");
  const [onlyMineFilter, setOnlyMineFilter] = useState<boolean>(false);
  const [showConnections, setShowConnections] = useState<boolean>(false);

  const [selectedView, setSelectedView] = useState("");
  const [selectedUserGroup, setSelectedUserGroup] = useState("");

  // Data states
  const [allTasks, setAllTasks] = useState<DbOrderManualTaskPlanningInfos[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [connections, setConnections] = useState<MbscEventConnection[]>([]);
  const [fetchedUsers, setFetchedUsers] = useState<Resource[]>([]);
  const [fetchedResources, setFetchedResources] = useState<Resource[]>([]);
  const [groupPlanningInfos, setGroupPlanningInfos] = useState<GroupPlanningInfos>();
  const [allUsers, setAllUsers] = useState<DbUserBasicInfos[]>([]);
  const [allResources, setAllResources] = useState<DbOrderManualTaskPlanningResourceInfos[]>([]);
  const [duplicateUserIds, setDuplicateUserIds] = useState<Record<number, string[]>>({});
  const [displayedUserIds, setDisplayedUserIds] = useState<string[]>([]);
  const [expandedResourcesNames, setExpandedResourcesNames] = useState<string[] | null>(null);
  const [todoTasks, setTodoTasks] = useState<DraggableToDoTask[]>([]);

  // Calendar states
  const [resources, setResources] = useState<Resource[]>([]);
  const [fetchedTask, setFetchedTask] = useState<DbOrderManualTaskPlanningInfos | null>(null);
  // #endregion states
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const { active: panelOpen, toggle: togglePanelOpen } = useToggle();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialDate = useMemo(() => {
    const storedSelectedDate = ReactSession.get(ReactSessionKeys.CALENDAR_SELECTEDDATE);
    if (storedSelectedDate) return new Date(storedSelectedDate);

    const dateParam = searchParams.get("date");
    return dateParam ? new Date(dateParam) : new Date();
  }, [searchParams]);

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  const getTaskStatusColor = (task: DbOrderManualTaskPlanningInfos, colorFilter: string) => {
    if (task.orderItemColor !== null && colorFilter === "project") return task.orderItemColor;

    switch (task.status!) {
      case ManualTaskPlanningStatus.Created:
        return "#8F8FA3";
      case ManualTaskPlanningStatus.ToDo:
        return "#3D6CE1";
      case ManualTaskPlanningStatus.InProgress:
        return "#FC7035";
      case ManualTaskPlanningStatus.Completed:
        return "#1FC19A";
      case ManualTaskPlanningStatus.Standby:
        return "#ED6273";
      // TODO : ask if this is good cause not specified in specs
      case ManualTaskPlanningStatus.Canceled:
      case ManualTaskPlanningStatus.Issue:
        return "#FCA464";
    }
  };

  const filteredEventsIds: string[] = useMemo(() => {
    let _filteredEvents: CalendarEvent[] = [...events];

    if (textFilter !== "")
      _filteredEvents = _filteredEvents.filter(
        (e) =>
          e.customerName.includesAllWords(textFilter) ||
          e.taskName.includesAllWords(textFilter) ||
          e.titleName.includesAllWords(textFilter)
      );

    if (customerFilter !== "") _filteredEvents = _filteredEvents.filter((e) => e.customerName === customerFilter);

    if (serviceFilter !== "") _filteredEvents = _filteredEvents.filter((e) => e.serviceName === serviceFilter);

    if (taskNameFilter !== "") _filteredEvents = _filteredEvents.filter((e) => e.taskName === taskNameFilter);

    if (assignedUserFilter !== "")
      _filteredEvents = _filteredEvents.filter((e) => (e.resource as string[]).includes(assignedUserFilter));

    if (assignedUserFilter !== "")
      _filteredEvents = _filteredEvents.filter((e) => (e.resource as string[]).includes(assignedUserFilter));

    if (onlyMineFilter) {
      const userId = `${currentUser?.id}${nameOfUserId}`;
      _filteredEvents = _filteredEvents.filter((e) => (e.resource as string[]).includes(userId));
    }

    return _filteredEvents.map((event) => event.id!.toString());
  }, [
    events,
    textFilter,
    customerFilter,
    serviceFilter,
    taskNameFilter,
    assignedUserFilter,
    assignedUserFilter,
    onlyMineFilter,
    currentUser?.id,
  ]);

  const filteredResources: Resource[] = useMemo(() => {
    let _filteredResources = [...resources];

    if (selectedView !== "") {
      const view = groupPlanningInfos?.viewGroups?.find((v) => v.name === selectedView);
      if (view) {
        const resourceIds = view.resourcesIds.map((id) => `${id}${nameOfResourceId}`);
        _filteredResources = _filteredResources.filter(
          (r) => !r.id.includes(nameOfResourceId) || resourceIds.includes(r.id)
        );
      }
    }

    return _filteredResources;
  }, [resources, selectedView, groupPlanningInfos]);

  const _view: MbscEventcalendarView = useMemo(() => {
    let view: MbscEventcalendarView = {};
    let type = "";

    switch (planningView) {
      case PlanningView.Day:
        type = "day";
        break;
      case PlanningView.Week:
        type = "week";
        break;
      case PlanningView.Month:
        type = "month";
        break;
      case PlanningView.Year:
        type = "year";
        break;
    }

    switch (planningMode) {
      case PlanningMode.Calendar:
        view.calendar = {
          type: type as "year" | "month" | "week" | undefined,
        };
        break;
      case PlanningMode.Agenda:
        view.agenda = {
          type: type as "month" | "year" | "week" | "day" | undefined,
        };
        break;
      case PlanningMode.Schedule:
        view.schedule = {
          type: type as "day" | "week" | "month" | undefined,
        };
        break;
      case PlanningMode.Timeline:
        view.timeline = {
          type: type as "day" | "week" | "month" | "year" | undefined,
          eventList: planningView === PlanningView.Month || planningView === PlanningView.Year,
        };
        break;
    }
    return view;
  }, [planningView, planningMode]);

  const viewableUsers = useMemo(() => {
    if (!groupPlanningInfos || !groupPlanningInfos.viewableGroupIds?.length) return allUsers;
    return allUsers
      .filter((x) => x.enabled && !x.deleted && groupPlanningInfos.viewableGroupIds!.includes(x.groupId!))
      .sort((a, b) => a.name!.localeCompare(b.name!));
  }, [allUsers, groupPlanningInfos]);

  const allowExternalDrop = useMemo(() => {
    return planningMode === PlanningMode.Timeline || planningMode === PlanningMode.Schedule;
  }, [planningMode]);

  // #region functions
  // Utils
  const getEventsAndConnectionsFromTask = (
    task: DbOrderManualTaskPlanningInfos,
    bypassAssignInfos: boolean = false
  ): { events: CalendarEvent[]; connections: MbscEventConnection[] } => {
    if (!task.events) return { events: [], connections: [] };
    const events: CalendarEvent[] = [];
    const connections: MbscEventConnection[] = [];

    task.events
      .filter((x) => bypassAssignInfos || (x.assignInfos?.date && x.assignInfos?.endDate))
      .forEach((event, index) => {
        const userId = event.assignInfos?.userId ? `${event.assignInfos?.userId}${nameOfUserId}` : null;
        const resourcesIds: string[] = [];
        if (event.resourceIds) resourcesIds.push(...event.resourceIds.map((id) => `${id}${nameOfResourceId}`));
        if (userId) resourcesIds.push(userId);

        const startDate = moment(event.assignInfos?.date || new Date())
          .local()
          .format();
        let endDate = moment(event.assignInfos?.endDate).local().format();
        if (!event.assignInfos?.endDate) {
          const [hours, minutes] = (defaultPlatformPlanningTaskDuration || "00:30").split(":").map(Number); // 30min default
          endDate = moment(startDate).add(hours, "hours").add(minutes, "minutes").local().format();
        }

        // push event
        events.push({
          // calendar props
          id: event.guid,
          title: event.name || task.taskName || "N/A",
          color: task.orderItemColor || getTaskStatusColor(task, "status"),
          start: startDate,
          end: endDate,
          allDay: event.allDay || false,
          resource: resourcesIds,
          editable:
            task.status !== ManualTaskPlanningStatus.Completed && task.status !== ManualTaskPlanningStatus.Canceled,
          // custom props
          taskId: task.id,
          customerName: task.customerName || "N/A",
          serviceName: task.serviceName || "N/A",
          taskName: task.taskName || "N/A",
          assignedUserId: userId,
          taskType: task.type!,
          titleName: task.titleName || "N/A",
          btCode: task.taskErpCode || "N/A",
          statusColor: getTaskStatusColor(task, "status"),
          eventAllDay: event.allDay,
          dueDate: task.orderDueDate,
          mpDuration: task.mpDuration,
        });
        if (index > 0)
          connections.push({
            from: task.events![index - 1].guid,
            to: event.guid,
            arrow: "to",
          });
      });
    return { events, connections };
  };

  // Used to get duplicates user ids and new generated ids for each duplicate.
  // We have to generate ids because Mobiscroll Scheduler use resources ids as key in react. As we have duplicates (users), React raises an error for each one of duplicate key.
  const getDuplicatesUserIdsNewIdsMapForUserGroup = (
    currentNode: GroupPlanningUserGroupInfos
  ): Record<number, string[]> => {
    // Recusrive function that retrieves all users ids
    const getAllUserIdsFromUserGroup = (currentNode: GroupPlanningUserGroupInfos): number[] => {
      const userIds = currentNode.usersIds ? [...currentNode.usersIds] : [];

      currentNode.childrens.forEach((child) => userIds.push(...getAllUserIdsFromUserGroup(child)));

      return userIds;
    };
    // Run recursive
    const userIds = getAllUserIdsFromUserGroup(currentNode);
    // Find duplicates
    const duplicates = FindDuplicates(userIds);
    // Init dico
    const output: Record<number, string[]> = {};
    // Generate Guid for each duplicate
    duplicates.forEach((duplicate) => {
      output[duplicate.value] = Array.from(Array(duplicate.count)).map((_) => uuidv4());
    });

    return output;
  };

  // Used to get resources from user group. ie tree of groups and users
  const getResourcesTreeFromUserGroup = (
    currentNode: GroupPlanningUserGroupInfos,
    duplicatesNewIds: Record<number, string[]>,
    duplicatesCount: Record<number, number>
  ): Resource => {
    // New id for node
    const newId = `${uuidv4()}${nameOfGroupId}`;
    // Create resource
    const resource: Resource = {
      id: newId,
      name: currentNode.name,
      eventCreation: false,
      collapsed: !expandedResourcesNames?.includes(currentNode.name),
      children: [],
    };

    // TODO : Here, we won't be able to keep the children order as in conf tree. To keep it, we should remove userIds array from GroupPlanningUserGroupInfos,
    // add a prop userId (-500 if group, user.id if user) and add a prop that gives us the type via an enum. Like that it's easier to keep and construct the tree

    // Traverse tree on resources
    currentNode.childrens.forEach((child) => {
      resource.children.push(getResourcesTreeFromUserGroup(child, duplicatesNewIds, duplicatesCount));
    });

    // manage user ids
    currentNode.usersIds.forEach((userId) => {
      // get user
      const userResource = fetchedUsers.find((u) => u.id.includes(userId.toString()));
      if (userResource) {
        // if user has duplicates (ie is in multiple groups of tree)
        if (duplicatesNewIds[userId]) {
          // push resource with one of new id (depending on count of occurences i.e. if we are on the third duplicate, we take the third generated id)
          resource.children.push({
            ...userResource,
            id: duplicatesNewIds[userId][duplicatesCount[userId]],
            collapsed: !expandedResourcesNames?.includes(userResource.name),
          });
          ++duplicatesCount[userId]; // increment duplicate
        } else {
          resource.children.push({ ...userResource, collapsed: !expandedResourcesNames?.includes(userResource.name) }); // if no duplicate, push user resource as it is
        }
      }
    });

    return resource;
  };

  // Used to replace original ids by duplicates ids i.e. ids that we generate for duplicates
  const replaceDuplicateIdsByNewIdsInEvent = (event: CalendarEvent, duplicatesNewIds: Record<number, string[]>) => {
    if (event.resource)
      // if no resource, no need to replace ids
      Object.keys(duplicatesNewIds).forEach((key) => {
        // loop on duplicate ids
        const indexOfValue = (event.resource as string[])?.indexOf(`${key}${nameOfUserId}`); // find index
        if (indexOfValue !== undefined && indexOfValue !== -1)
          // if index
          (event.resource as string[]).splice(indexOfValue, 1, ...duplicatesNewIds[key]); // remove old id and insert new ones
      });
  };

  // Used to replace duplicates ids by new ids on all event
  const replaceDuplicateIdsByNewIds = (duplicatesNewIds: Record<number, string[]>) => {
    const newEvents = [...events]; // copy event array
    // loop on events and replace
    newEvents.forEach((event) => {
      replaceDuplicateIdsByNewIdsInEvent(event, duplicatesNewIds);
    });
    // save events
    setEvents(newEvents);
  };

  // Opposite of function above
  const replaceNewIdsByOriginals = () => {
    const newEvents = [...events]; // copy event array

    // Init reverse duplicate => we are going to map new ids to their original id because it's easier
    const reversedDuplicates = {};
    Object.keys(duplicateUserIds).forEach((key) => {
      // loop on originals ids
      duplicateUserIds[key].forEach((value) => (reversedDuplicates[value] = key)); // push each new id as key and key as value
    });
    // loop on events
    newEvents.forEach((event) => {
      const idsToPush: number[] = []; // init new user ids to push
      if (event.resource) {
        // if no resource on event, no need to process
        Object.keys(reversedDuplicates).forEach((key) => {
          // loop on new ids
          const indexOfValue = (event.resource as string[])?.indexOf(key); // if id is present
          if (indexOfValue !== undefined && indexOfValue !== -1) {
            (event.resource as string[]).splice(indexOfValue, 1); // delete it from array
            if (!idsToPush.includes(reversedDuplicates[key])) idsToPush.push(reversedDuplicates[key]); // and if original not in array, push it
          }
        });
        (event.resource as string[]).push(...idsToPush.map((id) => `${id}${nameOfUserId}`)); // add array to resource of event
      }
    });
    // save events
    setEvents(newEvents);
  };

  const getAllUsersIdsFromUserGroup = (userGroup: GroupPlanningUserGroupInfos): string[] => {
    const result: string[] = [];

    result.push(...userGroup.usersIds.map((id) => `${id}${nameOfUserId}`));

    userGroup.childrens.forEach((child) => {
      result.push(...getAllUsersIdsFromUserGroup(child));
    });

    return result.filter(Distinct);
  };

  const getUserGroupResourceNameById = (id: string): string | null => {
    const traverseResources = (resource: Resource) => {
      if (!resource) return null;
      if (resource.id == id) return resource.name;
      if (resource.children && resource.children.length > 0)
        for (let i = 0; i < resource.children.length; i++) {
          const name = traverseResources(resource.children[i]);
          if (name != null) return name;
        }

      return null;
    };

    return traverseResources(resources[0]);
  };

  // Filters
  const handleSetResources = (usersResources: Resource[], resourcesResources: Resource[]) => {
    setResources([
      {
        id: "0",
        name: "Users",
        collapsed: false,
        eventCreation: false,
        children: usersResources,
      },
      {
        id: "1",
        name: "Resources",
        collapsed: false,
        eventCreation: false,
        children: resourcesResources,
      },
    ]);
  };

  const handleSelectUserGroupName = (userGroupName: string) => {
    setSelectedUserGroup(userGroupName);
    ReactSession.set(ReactSessionKeys.CALENDAR_USERGROUP, userGroupName);

    if (userGroupName === "") {
      // if no group selected
      replaceNewIdsByOriginals(); // replace new id by originals
      handleSetResources(fetchedUsers, fetchedResources); // set resources
      setDuplicateUserIds({}); // reset duplicate ids
      setDisplayedUserIds([]);
      return;
    }

    // get group
    const userGroup = groupPlanningInfos?.userGroups?.find((g) => g.name === userGroupName);
    if (!userGroup) return;
    // Set userIds to filter "Assigned to" filter value
    setDisplayedUserIds(getAllUsersIdsFromUserGroup(userGroup));
    // get duplicates from group
    const duplicatesNewIds = getDuplicatesUserIdsNewIdsMapForUserGroup(userGroup);
    // init count of processed duplicates
    const duplicatesCount = {};
    Object.keys(duplicatesNewIds).map((key) => (duplicatesCount[key] = 0));
    // get tree
    const userGroupResources = getResourcesTreeFromUserGroup(userGroup, duplicatesNewIds, duplicatesCount);
    handleSetResources(userGroupResources.children, fetchedResources); // set resources
    replaceDuplicateIdsByNewIds(duplicatesNewIds); // replace resource ids in events
    setDuplicateUserIds(duplicatesNewIds); // save computed duplicates
  };

  // Renders
  const renderCustomResource = (resource: Resource) => {
    const uniqueId = uuidv4();
    return (
      <div key={uniqueId} className="md-resource-header-template-cont">
        <div className="md-resource-header-template-name">{resource.name}</div>
      </div>
    );
  };

  const renderCustomEvent = (data: MbscCalendarEventData) => {
    const event = data.original as CalendarEvent;
    let backgroundColor = colorFilter === "project" ? data.color : event.statusColor;
    if (backgroundColor && backgroundColor[0] !== "#") backgroundColor = "#" + backgroundColor;

    const filtered: boolean = filteredEventsIds.includes(data.id);

    if (event.taskType === PlanningTaskType.TriggerOrderSchedule) {
      return (
        <Box
          sx={{
            borderColor: backgroundColor,
            color: isColorDark(backgroundColor) ? "text.primary" : "#000000",
            backgroundColor: `rgba(${hexToRgbString(backgroundColor ?? "FFFFFF")}, ${!filtered ? "0.2" : "1"})`,
          }}
        >
          <div className="mbsc-schedule-event-inner">
            <div className="mbsc-schedule-event-range mbsc-windows-dark mbsc-windows">{event.title}</div>
          </div>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          borderColor: backgroundColor,
          color: isColorDark(backgroundColor) ? "text.primary" : "#000000",
          backgroundColor: `rgba(${hexToRgbString(backgroundColor ?? "FFFFFF")}, ${!filtered ? "0.2" : "1"})`,
        }}
      >
        <div className="mbsc-schedule-event-inner">
          <div className="mbsc-schedule-event-title mbsc-windows-dark mbsc-windows">{event.titleName}</div>
          {event.mpDuration && (
            <div className="mbsc-schedule-event-title mbsc-windows-dark mbsc-windows custom-text-size">
              {t("page:body.programDuration")} : {event.mpDuration}
            </div>
          )}
          <div className="mbsc-schedule-event-range mbsc-windows-dark mbsc-windows custom-text-size">
            {t("page:body.client")} : {event.customerName}
          </div>
          <div className="mbsc-schedule-event-range mbsc-windows-dark mbsc-windows custom-text-size">
            {event.taskName}
          </div>
          <div className="mbsc-schedule-event-range mbsc-windows-dark mbsc-windows custom-text-size">
            BT : #{event.btCode}{" "}
            {event.dueDate && `| ${t("common:dueDate")} : ${moment(event.dueDate).format("DD-MM-YYYY")}`}
          </div>
        </div>
      </Box>
    );
  };

  const renderHeaderNavbar = () => {
    return (
      <>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={dayjs(selectedDate)}
              onChange={(newValue) => {
                setSelectedDate(newValue!.toDate());
                ReactSession.set(ReactSessionKeys.CALENDAR_SELECTEDDATE, newValue!.toDate());
              }}
              format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
              sx={{ mt: -3 }}
            />
          </LocalizationProvider>
          <Stack direction="row" spacing={2}>
            <ToggleButtonGroup value={planningMode} exclusive onChange={(_, value) => handlePlanningModeChange(value)}>
              <ToggleButton value="Calendar">{t("common:calendar")}</ToggleButton>
              <ToggleButton value="Agenda">{t("button:agenda")}</ToggleButton>
              <ToggleButton value="Schedule">{t("common:schedule")}</ToggleButton>
              <ToggleButton value="Timeline">{t("button:timeline")}</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={planningView} exclusive onChange={(_, value) => handlePlanningViewChange(value)}>
              <ToggleButton value="Day" disabled={planningMode === PlanningMode.Calendar}>
                {t("common:day")}
              </ToggleButton>
              <ToggleButton value="Week">{t("common:week")}</ToggleButton>
              <ToggleButton value="Month">{t("common:month")}</ToggleButton>
              <ToggleButton value="Year" disabled={planningMode === PlanningMode.Schedule}>
                {t("common:year")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <div className="md-custom-range-view-controls">
            <CalendarPrev />
            <CalendarToday />
            <CalendarNext />
          </div>
        </Stack>
      </>
    );
  };

  const renderLabel = (data: MbscCalendarEventData) => {
    const event = data.original as CalendarEvent;
    const color = colorFilter === "project" ? data.color : event.statusColor;

    const filtered: boolean = filteredEventsIds.includes(data.id);

    return (
      <div
        style={{
          borderColor: color,
          color: isColorDark(color) ? "text.primary" : "#333333",
          backgroundColor: `rgba(${hexToRgbString(color ?? "FFFFFF")}, ${!filtered ? "0.2" : "1"})`,
        }}
      >
        <div className="mbsc-calendar-label-inner mbsc-windows-dark mbsc-windows">
          <div className="mbsc-calendar-label-text mbsc-windows-dark mbsc-windows">
            {data.start} {event.title}
          </div>
        </div>
      </div>
    );
  };

  //Events
  const handleEventUpdate = (args: MbscEventUpdateEvent) => {
    const newEvent = args.event as CalendarEvent;
    const oldEvent = args.oldEvent as CalendarEvent;
    if (!newEvent?.resource || !oldEvent?.resource) return false;

    let oldId = (oldEvent.resource as string[])
      .filter((resourceId) => !(newEvent.resource as string[]).includes(resourceId))
      .at(0);
    let newId = (newEvent.resource as string[])
      .filter((resourceId) => !(oldEvent.resource as string[]).includes(resourceId))
      .at(0);

    if ((!oldId || !newId) && newEvent.start === oldEvent.start && newEvent.end === oldEvent.end) return false;

    const reversedDuplicates = {};
    Object.keys(duplicateUserIds).forEach((key) => {
      duplicateUserIds[key].forEach((value) => (reversedDuplicates[value] = key));
    });

    // manage ids if userGroup selected => ids are Guid
    if (oldId && IsGuid(oldId)) {
      oldId = `${reversedDuplicates[oldId]}${nameOfUserId}`;
    }

    if (newId && IsGuid(newId)) {
      newId = `${reversedDuplicates[newId]}${nameOfUserId}`;
    }

    if (!oldId?.includes(nameOfUserId) && newId?.includes(nameOfUserId)) {
      enqueueSnackbar(t("alert:warning.cannotDropTaskFromResourceToUser"), "warning");
      return false;
    }

    if (controlKeyDown) {
      const task = allTasks.find((x) => x.id === newEvent.taskId)!;
      const eventToDuplicate = task.events!.find((x) => x.guid === newEvent.id);
      if (eventToDuplicate) {
        const duplicatedEvent = {
          ...eventToDuplicate,
          assignInfos: { ...eventToDuplicate.assignInfos!, $id: -2 },
          claimInfos: { ...eventToDuplicate.claimInfos!, $id: -3 },
          guid: "00000000-0000-0000-0000-000000000000",
          $id: -1,
        };
        duplicatedEvent.assignInfos!.date = newEvent.start as Date;
        duplicatedEvent.assignInfos!.endDate = newEvent.end as Date;
        duplicatedEvent.assignInfos!.userId = parseInt(newId!);

        duplicatedEvent.claimInfos!.date = newEvent.start as Date;
        duplicatedEvent.claimInfos!.endDate = newEvent.end as Date;
        duplicatedEvent.claimInfos!.userId = parseInt(newId!);
        task.events!.push(duplicatedEvent);

        planningUserTaskService.UpdateTaskById({
          taskId: task.id,
          events: task.events,
          comment: null,
          name: null,
          status: null,
        });
      } else enqueueSnackbar(t("alert:error.eventNotFoundForDuplication"), "error");

      return false;
    }
  };

  const handleEventUpdated = (args: MbscEventUpdatedEvent) => {
    const newEvent = args.event;
    const oldEvent = args.oldEvent;
    if (!newEvent || !newEvent.resource || !oldEvent || !oldEvent.resource) return false;

    let oldId = (oldEvent.resource as string[])
      .filter((resourceId) => !(newEvent.resource as string[]).includes(resourceId))
      .at(0);
    let newId = (newEvent.resource as string[])
      .filter((resourceId) => !(oldEvent.resource as string[]).includes(resourceId))
      .at(0);

    if ((!oldId || !newId) && newEvent.start === oldEvent.start && newEvent.end === oldEvent.end) return false;

    // manage ids if userGroup selected => ids are Guid
    const reversedDuplicates = {};
    Object.keys(duplicateUserIds).forEach((key) => {
      duplicateUserIds[key].forEach((value) => (reversedDuplicates[value] = key));
    });

    if (oldId && IsGuid(oldId)) {
      oldId = `${reversedDuplicates[oldId]}${nameOfUserId}`;
    }

    if (newId && IsGuid(newId)) {
      newId = `${reversedDuplicates[newId]}${nameOfUserId}`;
    }

    let userId: number = -500;
    const resourcesIds = (newEvent.resource as string[])
      .filter((resourceId) => resourceId.includes(nameOfResourceId))
      .map((value) => parseInt(value));

    if (oldId?.includes(nameOfUserId)) {
      if (newId?.includes(nameOfUserId)) {
        // from user to user
        userId = parseInt(newId);
      } else {
        // from user to resource
        userId = parseInt(oldId);
      }
    } else {
      if (newId?.includes(nameOfUserId)) {
        // from resource to user
        // managed before but we return just in case
        return false;
      } else {
        // from resource to resource
        userId = parseInt(
          (oldEvent.resource as string[]).find((resourceId) => resourceId.includes(nameOfUserId)) || "-500"
        );
      }
    }

    planningUserTaskService.UpdateEventByTaskId(newEvent.taskId, newEvent.id!.toString(), {
      startDate: newEvent.start as Date,
      endDate: newEvent.end as Date,
      resourceIds: resourcesIds,
      userId: userId,
    });
  };

  const handleToDoTaskDrop = (args: MbscEventCreateEvent) => {
    const draggedEvent = args.event as CalendarEvent;

    if (!draggedEvent.resource) return false;

    // User Id
    let userId: string | undefined = undefined;
    if (Array.isArray(draggedEvent.resource))
      userId = (draggedEvent.resource as string[]).find((resourceId) => !resourceId.includes(nameOfResourceId));
    else if (typeof draggedEvent.resource === "string" && draggedEvent.resource.includes(nameOfUserId))
      userId = draggedEvent.resource;

    // Manage duplicates in case of userGroup selected
    if (userId && IsGuid(userId)) {
      const reversedDuplicates = {};
      Object.keys(duplicateUserIds).forEach((key) => {
        duplicateUserIds[key].forEach((value) => (reversedDuplicates[value] = key));
      });

      userId = reversedDuplicates[userId];
    }

    // Resource Ids
    let resourcesIds: number[] = [];
    if (Array.isArray(draggedEvent.resource))
      resourcesIds = (draggedEvent.resource as string[])
        .filter((resourceId) => resourceId.includes(nameOfResourceId))
        .map((value) => parseInt(value));
    else if (typeof draggedEvent.resource === "string" && draggedEvent.resource.includes(nameOfResourceId))
      resourcesIds = [parseInt(draggedEvent.resource)];

    // Get task
    const task = allTasks.find((x) => x.id === draggedEvent.taskId)!;
    if (!task) return false;
    // Update event in task
    const eventToUpdateIndex = task.events!.findIndex((x) => x.guid === draggedEvent.id);
    if (eventToUpdateIndex === -1) return false;

    task.events![eventToUpdateIndex].assignInfos = new DbObjectCreateOrUpdateInfos({
      id: -500,
      userId: userId ? parseInt(userId) : null,
      date: draggedEvent.start as Date,
      endDate: draggedEvent.end as Date,
    });
    task.events![eventToUpdateIndex].resourceIds = resourcesIds;

    planningUserTaskService.UpdateTaskById({
      taskId: task.id,
      events: task.events,
      comment: null,
      name: null,
      status: ManualTaskPlanningStatus.InProgress,
    });
  };

  const handleOnEventClick = (args: MbscEventClickEvent) => {
    clickEventCount.current = clickEventCount.current + 1;

    setTimeout(() => {
      if (clickEventCount.current === 1)
        planningUserTaskService.GetTaskById(args.event.taskId).then((response) => {
          if (response.task) {
            setFetchedTask(response.task);
            togglePanelOpen();
          } else enqueueSnackbar(t("alert:error.taskNotFound", { id: args.event.taskId }), "error");
          clickEventCount.current = 0;
        });
    }, 150);
  };

  const handleEventDoubleClick = (args: MbscEventClickEvent) => {
    const event = args.event as CalendarEvent;
    clickEventCount.current = clickEventCount.current + 2;

    planningUserTaskService.GetTaskById(args.event.taskId).then((response) => {
      if (response.task)
        openModal &&
          openModal<ModalEditTaskElementProps>(ModalEditTask, {
            task: response.task,
            users: viewableUsers,
            resources: allResources,
            eventToOpenGuid: event.id as string,
          });
      else enqueueSnackbar(t("alert:error.taskNotFound", { id: args.event.taskId }), "error");

      clickEventCount.current = 0;
    });
  };

  const handleResourceExpand = (args: MbscResourceExpandEvent) => {
    if (!args.resource) return;

    const resourceName = getUserGroupResourceNameById(args.resource as string);

    if (resourceName) {
      const newArray = [...(expandedResourcesNames || []), resourceName];
      setExpandedResourcesNames(newArray);
      ReactSession.set(ReactSessionKeys.CALENDAR_EXPANDEDRESOURCESNAMES, newArray);
    }
  };

  const handleResourceCollapse = (args: MbscResourceCollapseEvent) => {
    if (!args.resource) return;

    const resourceName = getUserGroupResourceNameById(args.resource as string);

    if (resourceName) {
      const newArray = [...(expandedResourcesNames || []).filter((x) => x !== resourceName)];
      setExpandedResourcesNames(newArray.length > 0 ? newArray : null);
      ReactSession.set(ReactSessionKeys.CALENDAR_EXPANDEDRESOURCESNAMES, newArray.length > 0 ? newArray : null);
    }
  };

  const getDateRange = (date: Date): [string, string] => {
    const inputDate = moment(date);
    const dateFormat = "YYYY-MM-DD";

    switch (planningView) {
      case PlanningView.Day:
        return [inputDate.format(dateFormat), inputDate.format(dateFormat)];
      case PlanningView.Week:
        return [
          inputDate.clone().startOf("week").format(dateFormat),
          inputDate.clone().endOf("week").format(dateFormat),
        ];
      case PlanningView.Month:
        return [
          inputDate.clone().startOf("month").format(dateFormat),
          inputDate.clone().endOf("month").format(dateFormat),
        ];
      case PlanningView.Year:
        return [
          inputDate.clone().startOf("year").format(dateFormat),
          inputDate.clone().endOf("year").format(dateFormat),
        ];
    }
  };

  // Todo tasks
  const handleOpenPanel = (taskId: number) => {
    setFetchedTask(todoTasks.find((x) => x.task.id === taskId)?.task || null);
    togglePanelOpen();
  };
  // #endregion functions

  // #region Handlers
  const handleCustomerFilterChange = (customerValue: string) => {
    setCustomerFilter(customerValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_CUSTOMER, customerValue);
  };

  const handleServiceFilterChange = (serviceValue: string) => {
    setServiceFilter(serviceValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_SERVICE, serviceValue);
  };

  const handleTaskNameFilterChange = (taskNameValue: string) => {
    setTaskNameFilter(taskNameValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_TASKNAME, taskNameValue);
  };

  const handleAssignedToFilterChange = (assignedToValue: string) => {
    setAssignedUserFilter(assignedToValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_ASSIGNED, assignedToValue);
  };

  const handleViewFilterChange = (selectedViewValue: string) => {
    setSelectedView(selectedViewValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_VIEW, selectedViewValue);
  };

  const handleColorFilterChange = (colorValue: "project" | "status") => {
    setColorFilter(colorValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_COLOR, colorValue);
  };

  const handleShowOnlyMyTasksFilterChange = (onlyMineValue: boolean) => {
    setOnlyMineFilter(onlyMineValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_ONLYMYTASKS, onlyMineValue);
  };

  const handleShowConnectionsFilterChange = (showConnectionsValue: boolean) => {
    setShowConnections(showConnectionsValue);
    ReactSession.set(ReactSessionKeys.CALENDAR_SHOWCONNECTIONS, showConnectionsValue);
  };
  const handlePlanningModeChange = (planningMode: PlanningMode, overridePlanningView?: PlanningView) => {
    // manage non supported cases
    const _planningView = overridePlanningView ?? planningView;
    if (planningMode === PlanningMode.Calendar && _planningView === PlanningView.Day)
      handlePlanningViewChange(PlanningView.Week);
    else if (planningMode === PlanningMode.Schedule && _planningView === PlanningView.Year)
      handlePlanningViewChange(PlanningView.Month);

    setPlanningMode(planningMode);
    ReactSession.set(ReactSessionKeys.CALENDAR_SELECTEDMODE, planningMode);
  };
  const handlePlanningViewChange = (planningView: PlanningView) => {
    setPlanningView(planningView);
    ReactSession.set(ReactSessionKeys.CALENDAR_SELECTEDVIEW, planningView);
  };
  // #endregion Handlers

  // #region listeners
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key == "Shift") setControlKeyDown(true);
  };
  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key == "Shift") setControlKeyDown(false);
  };

  useEventListener("keydown", handleKeyDown);
  useEventListener("keyup", handleKeyUp);
  // #endregion listeners

  // #region useEffects
  // Init
  useEffect(() => {
    let planningMode = cacheService.GetPlatformDefaultPlanningMode();
    let planningView = cacheService.GetPlatformDefaultPlanningView();

    const storedSelectedMode = ReactSession.get(ReactSessionKeys.CALENDAR_SELECTEDMODE);
    if (storedSelectedMode) planningMode = storedSelectedMode;

    const storedSelectedView = ReactSession.get(ReactSessionKeys.CALENDAR_SELECTEDVIEW);
    if (storedSelectedView) planningView = storedSelectedView;

    handlePlanningViewChange(planningView || PlanningView.Month);
    handlePlanningModeChange(planningMode || PlanningMode.Calendar, planningView);

    planningUserTaskService.GetGroupPlanningInfos().then((response) => {
      setGroupPlanningInfos(response.planningInfos);
    });
    Promise.all([planningUserTaskService.GetResourceList(), userService.GetAllUsersBasic()]).then(
      ([resourceResponse, userResponse]) => {
        const usersToAdd: Resource[] = [];
        const resourcesToAdd: Resource[] = [];

        if (!userResponse.success) {
          enqueueSnackbar(t("alert:error.fetchingUsers", { message: userResponse.message }), "error");
        } else if (userResponse.users) {
          const filteredUsers = userResponse.users
            .filter((x) => !x.deleted)
            .sort((a, b) => {
              const nameA = a.name || `${a.firstName} ${a.lastName}`;
              const nameB = b.name || `${b.firstName} ${b.lastName}`;

              return nameA.localeCompare(nameB);
            });
          setAllUsers(filteredUsers);
          filteredUsers.forEach((user) => {
            usersToAdd.push({
              id: `${user.id}${nameOfUserId}`,
              children: [],
              collapsed: false,
              eventCreation: true,
              name: user.name || `${user.firstName} ${user.lastName}`,
            });
          });
          setFetchedUsers(usersToAdd);
        }

        if (!resourceResponse.success) {
          enqueueSnackbar(t("alert:error.fetchingUsers", { message: userResponse.message }), "error");
        } else if (resourceResponse.resources) {
          setAllResources(resourceResponse.resources);
          resourceResponse.resources.forEach((resource) => {
            resourcesToAdd.push({
              id: `${resource.id}${nameOfResourceId}`,
              children: [],
              collapsed: false,
              eventCreation: true,
              name: resource.name,
            });
          });
          setFetchedResources(resourcesToAdd);
        }

        handleSetResources(usersToAdd, resourcesToAdd);

        const storedCustomerFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_CUSTOMER);
        if (storedCustomerFilterValue) setCustomerFilter(storedCustomerFilterValue);
        const storedServiceFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_SERVICE);
        if (storedServiceFilterValue) setServiceFilter(storedServiceFilterValue);
        const storedTaskNameFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_TASKNAME);
        if (storedTaskNameFilterValue) setTaskNameFilter(storedTaskNameFilterValue);
        const storedAssignedFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_ASSIGNED);
        if (storedAssignedFilterValue) setAssignedUserFilter(storedAssignedFilterValue);
        const storedViewFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_VIEW);
        if (storedViewFilterValue) setSelectedView(storedViewFilterValue);
        const storedColorFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_COLOR);
        if (storedColorFilterValue) setColorFilter(storedColorFilterValue);
        const storedOnlyMyTasksFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_ONLYMYTASKS);
        if (storedOnlyMyTasksFilterValue !== undefined && storedOnlyMyTasksFilterValue)
          setOnlyMineFilter(storedOnlyMyTasksFilterValue);
        const storedShowConnectionsFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_SHOWCONNECTIONS);
        if (storedShowConnectionsFilterValue !== undefined && storedShowConnectionsFilterValue)
          setShowConnections(storedShowConnectionsFilterValue);
        const storedExpandedResourcesNames = ReactSession.get(ReactSessionKeys.CALENDAR_EXPANDEDRESOURCESNAMES);
        if (storedExpandedResourcesNames) setExpandedResourcesNames(storedExpandedResourcesNames);
      }
    );

    // Get todos tasks
    planningUserTaskService.ListTasksByStatus(ManualTaskPlanningStatus.ToDo).then((response) => {
      if (!response.success) {
        enqueueSnackbar(t("alert:error.fetchingTasks", { message: response.message }), "error");
      } else if (response.tasks) {
        const draggableTasks: DraggableToDoTask[] = [];

        response.tasks.forEach((task) => {
          const eventsAndConnections = getEventsAndConnectionsFromTask(task, true);
          draggableTasks.push({
            task: task,
            events: eventsAndConnections.events,
            connections: eventsAndConnections.connections,
          });
        });

        setTodoTasks(draggableTasks);
        setAllTasks((prevState) => [...prevState, ...response.tasks!]);
      }
    });
  }, []);

  useEffect(() => {
    if (groupPlanningInfos && fetchedResources) {
      const storedUserGroupFilterValue = ReactSession.get(ReactSessionKeys.CALENDAR_USERGROUP);
      if (storedUserGroupFilterValue) handleSelectUserGroupName(storedUserGroupFilterValue);
    }
  }, [groupPlanningInfos, fetchedResources]);

  // handle search params
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (!dateParam || selectedDate.toISOString() === dateParam) return;

    const newSearchParameters = new URLSearchParams();
    newSearchParameters.set("date", selectedDate.toISOString());
    setSearchParams(newSearchParameters);
  }, [selectedDate]);

  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (!dateParam || selectedDate.toISOString() === dateParam) return;

    setSelectedDate(new Date(dateParam));
  }, [searchParams.get("date")]);

  // handle selectedDate
  useEffect(() => {
    const [startDate, endDate] = getDateRange(selectedDate);
    planningUserTaskService.ListTasksByDateRange(startDate, endDate).then((response) => {
      if (!response.success) {
        enqueueSnackbar(t("alert:error.fetchingTasks", { message: response.message }), "error");
      } else if (response.tasks) {
        const events: CalendarEvent[] = [];
        const connections: MbscEventConnection[] = [];

        response.tasks.forEach((task) => {
          const eventsAndConnections = getEventsAndConnectionsFromTask(task);
          events.push(...eventsAndConnections.events);
          connections.push(...eventsAndConnections.connections);
        });

        setAllTasks(response.tasks);
        setEvents(events);
        setConnections(connections);
        if (isLoading) setIsLoading(false);
      }
    });
  }, [selectedDate, planningView]);

  // WebSocket
  useEffect(() => {
    calendarNotifier.Connect().then(() => {
      console.info("::calendar websocket connected!");

      if (calendarNotifier.connection) {
        calendarNotifier.connection.on("CalendarMessage", (data) => {
          const task = data.data as DbOrderManualTaskPlanningInfos;

          const taskIndex = allTasks.findIndex((x) => x.id === task.id);
          if (taskIndex > -1) {
            const newAllTasks = [...allTasks];
            newAllTasks[taskIndex] = task;
            setAllTasks(newAllTasks);
          } else setAllTasks([...allTasks, task]);

          const eventsAndConnections = getEventsAndConnectionsFromTask(task);
          const eventsGuids = eventsAndConnections.events.map((e) => e.id!);

          // if user group selected and duplicates found, replace ids in new events
          if (
            selectedUserGroup !== "" &&
            (data.op === EventNotifierOperation.Create || data.op === EventNotifierOperation.Update)
          )
            eventsAndConnections.events.forEach((event) => replaceDuplicateIdsByNewIdsInEvent(event, duplicateUserIds));

          switch (data.op) {
            case EventNotifierOperation.Create:
              {
                setEvents((prevState) => [...prevState, ...eventsAndConnections.events]);
                setConnections((prevState) => [...prevState, ...eventsAndConnections.connections]);

                // Todo tasks
                if (task.status === ManualTaskPlanningStatus.ToDo) {
                  setTodoTasks((prevState) => {
                    const newTask = {
                      task: task,
                      events: eventsAndConnections.events,
                      connections: eventsAndConnections.connections,
                    };
                    return [...prevState, newTask];
                  });
                }
                console.debug("ws::calendar task add with Id = " + task.id);
              }
              break;
            case EventNotifierOperation.Update:
              {
                setEvents((prevState) => {
                  const newState = [...prevState].filter((x) => x.taskId !== task.id || eventsGuids.includes(x.id!));
                  eventsAndConnections.events.forEach((event) => {
                    const eventIndex_update = prevState.findIndex((e) => e.id === event.id);
                    if (eventIndex_update !== -1) {
                      newState[eventIndex_update] = event;
                    } else {
                      newState.push(event);
                    }
                  });
                  return newState;
                });

                const newConnections = [
                  ...connections.filter((c) => !eventsGuids.includes(c.from) && !eventsGuids.includes(c.to)),
                ];
                newConnections.push(...eventsAndConnections.connections);
                setConnections(newConnections);

                // Todo tasks
                if (task.status === ManualTaskPlanningStatus.ToDo) {
                  setTodoTasks((prevState) => {
                    const taskIndex = prevState.findIndex((x) => x.task.id === task.id);
                    if (taskIndex > -1) {
                      const newTask = {
                        task: task,
                        events: eventsAndConnections.events,
                        connections: eventsAndConnections.connections,
                      };
                      prevState[taskIndex] = newTask;
                    }
                    return [...prevState];
                  });
                } else if (task.status === ManualTaskPlanningStatus.InProgress) {
                  const todoTaskIndex = todoTasks.findIndex((x) => x.task.id === task.id);
                  if (todoTaskIndex > -1) {
                    setTodoTasks((prevState) => [...prevState].filter((x) => x.task.id !== task.id));
                  }
                }

                console.debug("ws::calendar task update with Id = " + task.id);
              }
              break;
            case EventNotifierOperation.Delete:
              {
                setEvents((prevState) => [...prevState].filter((e) => e.taskId === task.id));

                const newConnections = [
                  ...connections.filter((c) => !eventsGuids.includes(c.from) && !eventsGuids.includes(c.to)),
                ];
                setConnections(newConnections);

                // Todo tasks
                if (task.status === ManualTaskPlanningStatus.ToDo) {
                  setTodoTasks((prevState) => [...prevState].filter((x) => x.task.id !== task.id));
                }

                console.debug("ws::calendar task delete with Id = " + task.id);
              }
              break;
          }
        });
      }
    });

    return () => {
      calendarNotifier.Disconnect().then(() => {
        console.info("::calendar websocket disconnected");
      });
    };
  }, [allTasks]);

  // Misc
  useEffect(() => {
    scheduleRef.current?.refresh();
  }, [resources, events]);
  // #endregion useEffects

  return (
    <Stack sx={{ width: "100%", height: "100%" }} spacing={2}>
      <ListPageHeader mb={0} mt={1}>
        <ListPageHeaderGroupActions>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<HandshakeOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={customerFilter}
            onChange={(value) => handleCustomerFilterChange(value.target.value)}
          >
            <MenuItem value={""}>{t("common:customer")}</MenuItem>
            {events
              .map((item) => item.customerName)
              .filter(Distinct)
              .map((item, index) => (
                <MenuItem value={item!} key={index}>
                  {item}
                </MenuItem>
              ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<MergeTypeIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={serviceFilter}
            onChange={(value) => handleServiceFilterChange(value.target.value)}
          >
            <MenuItem value={""}>{t("menu:item.service")}</MenuItem>
            {events
              .map((item) => item.serviceName)
              .filter(Distinct)
              .map((item, index) => (
                <MenuItem value={item!} key={index}>
                  {item}
                </MenuItem>
              ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<AssignmentIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={taskNameFilter}
            onChange={(value) => handleTaskNameFilterChange(value.target.value)}
          >
            <MenuItem value={""}>{t("field:label.taskName")}</MenuItem>
            {events
              .map((item) => item.taskName)
              .filter(Distinct)
              .map((item, index) => (
                <MenuItem value={item!} key={index}>
                  {item}
                </MenuItem>
              ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<PersonOutlineOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={assignedUserFilter}
            onChange={(event) => handleAssignedToFilterChange(event.target.value)}
          >
            <MenuItem value={""}>{t("field:label.assignedTo")}</MenuItem>
            {fetchedUsers
              .filter((x) => !displayedUserIds.length || displayedUserIds.includes(x.id))
              .map((user, index) => (
                <MenuItem value={user.id} key={index}>
                  {user.name}
                </MenuItem>
              ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<VisibilityOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={selectedView}
            onChange={(event) => handleViewFilterChange(event.target.value as string)}
          >
            <MenuItem value="">{t("menu:item.view")}</MenuItem>
            {groupPlanningInfos?.viewGroups?.map((viewGroup, index) => (
              <MenuItem key={index} value={viewGroup.name}>
                {viewGroup.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<VisibilityOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={selectedUserGroup}
            onChange={(event) => handleSelectUserGroupName(event.target.value as string)}
          >
            <MenuItem value="">{t("menu:item.userGroup")}</MenuItem>
            {groupPlanningInfos?.userGroups?.map((userGroup, index) => (
              <MenuItem key={index} value={userGroup.name}>
                {userGroup.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<PaletteIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={colorFilter}
            onChange={(event) => handleColorFilterChange(event.target.value === "project" ? "project" : "status")}
          >
            <MenuItem key={"project"} value={"project"}>
              {t("menu:item.colorByProject")}
            </MenuItem>
            <MenuItem key={"status"} value={"status"}>
              {t("menu:item.colorByStatus")}
            </MenuItem>
          </Select>
          <FormGroup sx={{ pl: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyMineFilter}
                  onChange={(_, checked) => handleShowOnlyMyTasksFilterChange(checked)}
                />
              }
              label={<Typography variant="body2">{t("field:label.showOnlyMyTasks")}</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showConnections}
                  onChange={(_, checked) => handleShowConnectionsFilterChange(checked)}
                />
              }
              label={<Typography variant="body2">{t("field:label.showEventsConnections")}</Typography>}
            />
          </FormGroup>
        </ListPageHeaderGroupActions>
        <ListPageHeaderRight>
          <OutlinedInput
            label={t("common:search")}
            placeholder={t("common:search")}
            value={textFilter}
            onChange={(value) => setTextFilter(value.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
        </ListPageHeaderRight>
      </ListPageHeader>
      <ListPageHeaderLeft>
        <Button
          startIcon={expandDraggableTask ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={() => setExpandDraggableTask((prevState) => !prevState)}
          variant="outlined"
        >
          {t("button:showTodo")}
        </Button>
      </ListPageHeaderLeft>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Grid container>
          {expandDraggableTask && (
            <Grid item md={expandDraggableTask ? 3 : 0}>
              <Box sx={{ height: "calc(100vh - 250px)", overflow: "auto" }}>
                <Stack spacing={1} pr={1}>
                  {todoTasks.map((data) => (
                    <DraggableTask
                      key={data.task.id}
                      data={data}
                      handleOpenPanel={handleOpenPanel}
                      viewableUsers={viewableUsers}
                      allResources={allResources}
                      allowDrag={allowExternalDrop}
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>
          )}
          <Grid item md={expandDraggableTask ? 9 : 12}>
            <Box sx={{ height: "calc(100vh - 250px)" }}>
              <Eventcalendar
                ref={scheduleRef}
                // Date management
                selectedDate={selectedDate}
                onSelectedDateChange={(args) => {
                  if ((args.date as Date).isValid()) setSelectedDate(args.date as Date);
                }}
                locale={localeFr}
                // Theme management
                theme="windows"
                themeVariant={theme.palette.mode}
                className="md-resource-grouping-hierarchy"
                // Features management
                clickToCreate={false}
                dragToCreate={false}
                dragToMove={true}
                dragTimeStep={15}
                dragToResize={true}
                eventDelete={false}
                externalDrop={allowExternalDrop}
                // Data
                view={_view}
                data={events}
                resources={filteredResources}
                connections={showConnections ? connections : []}
                // Custom
                //renderResource={renderCustomResource}
                renderLabel={renderLabel} // Custom label in Calendar and agenda
                renderScheduleEvent={renderCustomEvent} // only Schedule + Timeline
                renderHeader={renderHeaderNavbar}
                // Events
                onEventUpdate={handleEventUpdate}
                onEventUpdated={handleEventUpdated}
                onEventClick={handleOnEventClick}
                onEventDoubleClick={handleEventDoubleClick}
                onResourceExpand={handleResourceExpand}
                onResourceCollapse={handleResourceCollapse}
                onEventCreate={handleToDoTaskDrop}
              />
            </Box>
          </Grid>
        </Grid>
      )}
      <UserTaskPlanningPanel
        task={fetchedTask || undefined}
        users={viewableUsers}
        resources={allResources}
        open={panelOpen}
        onClose={togglePanelOpen}
      />
    </Stack>
  );
}

export default PlanningCalendar;

function DraggableTask(props: {
  data: DraggableToDoTask;
  handleOpenPanel: (taskId: number) => void;
  viewableUsers: DbUserBasicInfos[];
  allResources: DbOrderManualTaskPlanningResourceInfos[];
  allowDrag: boolean;
}) {
  const { data, handleOpenPanel, viewableUsers, allResources, allowDrag } = props;

  const [draggable, setDraggable] = useState<HTMLDivElement>();

  const setDragElm = useCallback((elm: HTMLDivElement) => {
    setDraggable(elm);
  }, []);

  return (
    <div ref={setDragElm}>
      <KanbanTaskCard task={data.task} onClick={handleOpenPanel} users={viewableUsers} resources={allResources} />
      {data.events.length > 0 && allowDrag && (
        <Draggable
          dragData={data.events[0]}
          element={draggable}
          cssClass={undefined}
          responsive={undefined}
          theme={""}
          themeVariant={undefined}
        />
      )}
    </div>
  );
}
